import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IEpisode, IBgm } from '@packages/global';
import { MusicalNoteIcon, XMarkIcon, ListBulletIcon, TrashIcon } from '@heroicons/react/24/outline';
import { episodeApi } from '../../../services/user/episode.api';
import { useDialog } from '../../../hooks/useDialog';
import { useToast } from '../../../stores/toastStore';
import AudioUpload from '../../common/AudioUpload';
import AudioPlayer from '../../common/AudioPlayer';
import MusicSelector from '../../common/MusicSelector';

interface BgmTabProps {
  episode: IEpisode;
  refreshEpisode?: () => Promise<void>;
  onRequestClose?: () => void;
}

export default function BgmTab({ episode, refreshEpisode, onRequestClose }: BgmTabProps) {
  const { t } = useTranslation(['editor', 'common']);
  const [showMusicSelector, setShowMusicSelector] = useState(false);
  const [saving, setSaving] = useState(false);
  const { confirm } = useDialog();
  const { success, error } = useToast();

  const updateBgm = async (bgmUrl: string) => {
    try {
      setSaving(true);
      await episodeApi.updateEpisode(episode.id, { bgmUrl });
      if (refreshEpisode) {
        await refreshEpisode();
      }
      success(bgmUrl ? t('editor:common.bgmUpdated') : t('editor:bgm.cleared'));
    } catch (err) {
      console.error('更新背景音乐失败:', err);
      error(t('editor:common.bgmUpdateFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleSelectMusic = async (bgm: IBgm) => {
    if (!bgm.audioUrl) {
      error(t('editor:common.bgmUpdateFailed'));
      return;
    }

    await updateBgm(bgm.audioUrl);
    setShowMusicSelector(false);
  };

  const handleClearBgm = async () => {
    const confirmed = await confirm(t('editor:bgm.removeConfirm'), t('editor:bgm.remove'), 'warning');
    if (!confirmed) return;
    await updateBgm('');
  };

  return (
    <div className="flex flex-col h-full max-h-[90vh] bg-bg-surface text-gray-50">
      <div className="flex items-center justify-between gap-4 px-6 py-3 border-b border-border-subtle">
        <div className="flex items-baseline gap-3 min-w-0">
          <h2 className="text-lg font-semibold shrink-0">BGM</h2>
          <p className="text-sm text-gray-400 truncate">{episode.title || t('editor:bgm.noSelection')}</p>
        </div>
        <button
          onClick={onRequestClose}
          className="inline-flex items-center justify-center w-10 h-10 rounded-lg border border-border-subtle bg-bg-elevated hover:bg-border-default text-gray-200 shrink-0"
          title={t('common:close', { defaultValue: '关闭' })}
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-5xl mx-auto grid grid-cols-1 xl:grid-cols-[1.05fr_0.95fr] gap-5">
          <div className="flex flex-col gap-4">
            <div className="rounded-2xl border border-border-subtle bg-bg-surface p-5">
              <div className="flex items-center gap-2 mb-3 text-gray-300">
                <MusicalNoteIcon className="w-4 h-4 text-purple-400" />
                <h3 className="text-sm font-medium">{t('editor:bgm.currentTrack')}</h3>
              </div>

              <div className="rounded-xl bg-bg-form border border-border-subtle p-4 min-h-[96px] flex items-center">
                {episode.bgmUrl ? (
                  <div className="flex items-center gap-3 w-full">
                    <MusicalNoteIcon className="h-5 w-5 text-purple-400 flex-shrink-0" />
                    <AudioPlayer key={episode.bgmUrl} src={episode.bgmUrl} className="w-full h-8 flex-1" />
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">{t('editor:bgm.noSelection')}</p>
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-border-subtle bg-bg-surface p-5 mt-auto">
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => setShowMusicSelector(true)}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-bg-elevated hover:bg-border-default disabled:bg-bg-elevated disabled:cursor-not-allowed text-gray-50 text-sm rounded-lg transition-colors"
                >
                  <ListBulletIcon className="w-4 h-4" />
                  {t('common:select')}
                </button>

                <AudioUpload
                  onUploadSuccess={(url) => updateBgm(url)}
                  mode="button"
                  buttonText={t('common:upload')}
                  buttonClassName="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors"
                />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-2xl border border-border-subtle bg-bg-surface p-5">
              <h3 className="text-sm font-medium text-gray-300 mb-4">{t('editor:bgm.manageTitle')}</h3>
              <p className="text-sm leading-6 text-gray-400">{t('editor:bgm.manageHelp')}</p>
            </div>

            {episode.bgmUrl && (
              <div className="rounded-2xl border border-red-900/40 bg-red-950/15 p-5">
                <button
                  onClick={handleClearBgm}
                  disabled={saving}
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-sm rounded-xl transition-colors"
                >
                  <TrashIcon className="w-4 h-4" />
                  {t('editor:bgm.remove')}
                </button>
                <p className="mt-2 text-xs leading-5 text-gray-400">{t('editor:bgm.removeConfirm')}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <MusicSelector
        isOpen={showMusicSelector}
        onClose={() => setShowMusicSelector(false)}
        onSelect={handleSelectMusic}
      />
    </div>
  );
}
